import { ColorSwatchIcon, PhotographIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteData, putData, uploadFile } from "../backend";
import { useModal } from "../contexts";
import useUser from "../hooks/useUser";
import ErrorModal from "./ErrorModal";
import SmallTextInputField from "./SmallTextInputField";
import PayWallModalTeams from "./PayWallModalTeams";


function SettingsBranding() {
  const { t } = useTranslation();
  const { openModal } = useModal();

  const { user, refreshUser } = useUser();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [deleteLogo, setDeleteLogo] = useState<boolean>(false);

  const [team, setTeam] = useState(user!.activeTeam!);

  const [saveState, setSaveState] = useState<"INITIAL" | "PENDING" | "SAVING" | "SAVED">("INITIAL");

  const imageBase64 = logoFile ? URL.createObjectURL(logoFile) : team?.brandingLogo;

  function updateBrandingColour(field: "brandingColour" | "brandingColourText", value: string) {
    value = value.replaceAll(/[^0-9a-fA-F]/g, "").slice(0, 6).toLowerCase();
    value = "#" + value;

    setTeam(t => ({ ...t, [field]: value }))

    if (value.length === 7)
      setSaveState("PENDING");
    else
      setSaveState("INITIAL");
  }

  function handleRemoveLogo() {
    setSaveState("PENDING");
    setDeleteLogo(true);
  }

  function handleUploadLogo(file: any) {
    setSaveState("PENDING");
    setDeleteLogo(false);

    setLogoFile(file);
  }

  async function handleSave() {
    setSaveState("SAVING");

    try {
      if (logoFile)
        await uploadFile("/auth/team/upload/", logoFile);

      if (deleteLogo && user?.activeTeam?.brandingLogo)
        await deleteData("/auth/team/delete-logo/");

      await putData("/auth/team/", team)
        .then((team) => {
          refreshUser((user: any) => ({ ...user, team }));
        })

      setSaveState("SAVED");

    } catch (error) {
      openModal(<ErrorModal details={error} />)
    }
  }

  if (!user?.subscription?.featureTeams)
    return (
      <div className="flex flex-col items-center justify-center flex-1">
        <ColorSwatchIcon className="w-6 h-6 text-gray-500" />
        <h4 className="text-lg font-gilroy font-bold">{t("Custom Team's Branding")}</h4>
        <p className="text-base">{t("Upgrade to Teams to set custom branding for customer facing pages.")}</p>
        <button className="btn btn-blue mt-8 py-3 w-60" onClick={() => openModal(<PayWallModalTeams />)}>{t("Upgrade")}</button>
      </div>
    );

  return (
    <div className="flex flex-col gap-4 p-8 w-full">
      <div className="flex items-end">
        <div className="flex flex-col">
          <div className="font-gilroy font-semibold text-lg">
            {t("Branding")}
          </div>
          <div className="text-gray-400 text-sm font-semibold">
            {t("Branding can be provided to customize your public facing eSignature portal")}
          </div>
        </div>
      </div>

      <div className="flex gap-8">
        <div className="flex flex-col gap-2 items-end">

          <div className="flex items-end">
            <div className="font-gilroy font-semibold text-gray-600 text-lg mr-1">#</div>
            <SmallTextInputField label={t("Brand Color")} className="font-mono" value={team.brandingColour.replace("#", "")} onChange={value => updateBrandingColour("brandingColour", value)} />
            <div className="ml-2 w-8 h-8 rounded shadow bg-gray-100" style={{ backgroundColor: team.brandingColour }} />
          </div>

          <div className="flex items-end">
            <div className="font-gilroy font-semibold text-gray-600 text-lg mr-1">#</div>
            <SmallTextInputField label={t("Text Color")} className="font-mono" value={team.brandingColourText.replace("#", "")} onChange={value => updateBrandingColour("brandingColourText", value)} />
            <div className="ml-2 w-8 h-8 rounded shadow bg-gray-100" style={{ backgroundColor: team.brandingColourText }} />
          </div>
        </div>

        <div className="px-12 rounded flex items-center justify-center font-semibold text-nowrap" style={{ backgroundColor: team.brandingColour, color: team.brandingColourText }}>
          This is an example
        </div>
      </div>

      <div className="flex flex-col w-fit gap-2 mt-4">
        <div className="flex items-end gap-4">
          <div className="font-gilroy font-semibold text-gray-600 text-sm mr-auto">
            Logo (400 x 400px)
          </div>
          {(imageBase64 ?? team.brandingLogo) && !deleteLogo &&
            <button className="text-sm hover:underline cursor-pointer" onClick={() => handleRemoveLogo()}>
              {t("Remove")}
            </button>
          }
          <label className="text-sm hover:underline cursor-pointer">
            {t("Upload")}
            <input type="file" className="invisible absolute" accept="image/*" onChange={e => handleUploadLogo(e.currentTarget.files![0])} />
          </label>

        </div>

        <div className="relative group w-[320px] h-[320px] bg-gray-50 p-5 flex items-center justify-center">
          {(imageBase64 ?? team.brandingLogo) && !deleteLogo
            ? <img src={imageBase64 ?? team.brandingLogo} className="w-[400px] object-contain" alt="Logo" />
            : <PhotographIcon className="w-40 h-40 text-gray-400" />
          }
        </div>
      </div>

      <button className="btn btn-blue text-sm w-40 px-2 py-1 transition-colors" disabled={saveState !== "PENDING"} onClick={handleSave}>
        {saveState === "SAVING" ? t("Saving...") : (saveState === "SAVED" ? t("Saved") : t("Save"))}
      </button>
    </div>
  );
}

export default SettingsBranding;
